import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useDeviceOrientation from '@lib/hooks/useDeviceOrientation';
import RequestLandscapeModal from '@components/Sandbox/RequestLandscapeModal';
import NoticeBubble from '@atoms/NoticeBubble';
import { useOffice } from '@components/OfficeLoader';
import { getUserAccountById } from '@services/youtube-platform.service';
import { isInIframe } from '@embed/helpers';

// Phones only. iPad and Android tablets are deliberately excluded
// (Android tablets omit "Mobile" from their UA; iPads don't say "iPhone").
const PHONE_UA = /iPhone|iPod|Android.*Mobile|Windows Phone|IEMobile|BlackBerry|webOS|Opera Mini/i;

// Pages that already handle small screens themselves (device-width viewport,
// or their own sandbox notice) or are not part of the logged-in app.
const SKIP_PATHS = [
  '/login',
  '/signup',
  '/oauth/authorize',
  '/sandbox',
];

const DISMISSED_KEY = 'mobile-device-notice-dismissed';

/**
 * Mirrors the sandbox's "rotate to landscape" modal and "best seen on a larger
 * screen" bubble for the app itself — phones only, and only once onboarding is
 * done (OnBoarding sets a device-width viewport and is mobile-friendly already).
 */
export default function MobileDeviceNotice() {
  const router = useRouter();
  const { isPowerpoint } = useOffice();
  const { orientation } = useDeviceOrientation();
  const [isPhone, setIsPhone] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(false);

  useEffect(() => {
    const phone = PHONE_UA.test(navigator.userAgent);
    setIsPhone(phone);
    setBubbleVisible(phone && sessionStorage.getItem(DISMISSED_KEY) !== 'true');
  }, []);

  const onboardingRoute = router.query?.isOnboarding === 'true';

  // `new_user` stays true until onboarding finishes. Re-check on navigation
  // until we see it done, then stop asking.
  useEffect(() => {
    if (!isPhone || onboardingDone) return;
    if (onboardingRoute) {
      setOnboardingDone(false);
      return;
    }

    let cancelled = false;
    getUserAccountById()
      .then((res: any) => {
        if (cancelled) return;
        if (res?.status && !res.entity?.new_user) setOnboardingDone(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isPhone, onboardingDone, onboardingRoute, router.pathname]);

  const skip =
    !isPhone ||
    !onboardingDone ||
    onboardingRoute ||
    isPowerpoint ||
    isInIframe() ||
    SKIP_PATHS.some((p) => router.pathname.startsWith(p));

  if (skip) return null;

  const dismissBubble = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setBubbleVisible(false);
  };

  return (
    <>
      <RequestLandscapeModal visible={orientation === 'Portrait'} />
      <NoticeBubble
        visible={bubbleVisible}
        onClose={dismissBubble}
        message="This app is best seen on a larger screen."
      />
    </>
  );
}
